import React from 'react';

import { formatMoney, toMoney } from '@betterment/core';
import { Box, ListRow, Separator, Text, Tooltip } from '@betterment/samba-react';

import { ANNUAL_FOUR_OH_ONE_K_CONTRIBUTION_FREQUENCY } from './data';

import type { EmployeeFourOhOneKContributionsResult, EmployersResult } from './data';

const NOT_PROJECTED_TO_MAX = 'NotProjectedToMax';
const PROJECTED_TO_MAX = 'ProjectedToMax';
const MAXED = 'Maxed';
type FourOhOneKMaxOutStatuses = typeof NOT_PROJECTED_TO_MAX | typeof PROJECTED_TO_MAX | typeof MAXED;

const matchIsAnnual = (employer: EmployersResult[number]): boolean => {
  if (!employer.contributionConfig) {
    return false;
  }
  return employer.contributionConfig.frequency === ANNUAL_FOUR_OH_ONE_K_CONTRIBUTION_FREQUENCY;
};

const displayMatchAmount = (employer: EmployersResult[number]): number => {
  if (!employer.fourOhOneKContributions) {
    return 0;
  }
  if (matchIsAnnual(employer)) {
    return employer.fourOhOneKContributions.thisYear.projection?.total?.cents || 0;
  }
  return employer.fourOhOneKContributions.thisYear.amount.cents || 0;
};

const willReceiveFullMatch = (employer: EmployersResult[number]): boolean => {
  if (!employer.contributionConfig) {
    return false;
  }
  return displayMatchAmount(employer) >= employer.contributionConfig.limit?.cents;
};

const AnnualMatchTooltipCopy = (taxYear: number): string => {
  return `Your projected match is based on your ${taxYear} contributions, and will be paid in ${taxYear + 1}.`;
};

const hideEmployerMatch = (employer: EmployersResult[number]): boolean => {
  return !employer.contributionConfig && !employer.fourOhOneKContributions?.thisYear?.amount?.cents;
};

const maxMatchText = (employer: EmployersResult[number]): string => {
  if (matchIsAnnual(employer)) {
    return 'Projected to receive full match';
  }
  return 'Receiving full match';
};

const EmployerMatchHeader = (
  employer: EmployersResult[number],
  {
    thisYear: {
      taxYear: { year },
    },
  }: EmployeeFourOhOneKContributionsResult
) => {
  const { soloFourOhOneKPlan } = employer;

  if (matchIsAnnual(employer)) {
    return (
      <Box css={{ marginRight: 'space.400', display: 'grid' }}>
        <Box css={{ display: 'flex', gap: 'space.50' }}>
          <Text>Projected employer match</Text>
          <Tooltip>
            <Tooltip.Trigger asChild={true}>
              <Tooltip.IconButton data-testid="matchTooltip" />
            </Tooltip.Trigger>
            <Tooltip.Content align="start" side="left">
              <Tooltip.Text>{AnnualMatchTooltipCopy(year)}</Tooltip.Text>
            </Tooltip.Content>
          </Tooltip>
        </Box>
        {willReceiveFullMatch(employer) && (
          <Text color="text.success" variant="text50">
            {maxMatchText(employer)}
          </Text>
        )}
      </Box>
    );
  }

  return (
    <Text>
      {year} employer {soloFourOhOneKPlan ? 'contributions' : 'match'}
    </Text>
  );
};

const fourOhOneKLimitMaxOutProjection = (
  employeeContributions: EmployeeFourOhOneKContributionsResult
): FourOhOneKMaxOutStatuses => {
  const contributionLimit = employeeContributions.thisYear.taxYear.fourOhOneKContributionLimit.cents;
  const currentContributionAmount = employeeContributions.thisYear.result.amount.cents;
  const projectedContributionAmount = employeeContributions.thisYear.result.projection?.total?.cents || 0;

  if (currentContributionAmount >= contributionLimit) {
    return MAXED;
  }
  if (projectedContributionAmount >= contributionLimit) {
    return PROJECTED_TO_MAX;
  }
  return NOT_PROJECTED_TO_MAX;
};

const fourOhOneKLimitTooltipCopy = (employeeContributions: EmployeeFourOhOneKContributionsResult): string => {
  if (employeeContributions.thisYear.taxYear.isEligibleForFourOhOneKContributionCatchUp) {
    return 'Limit includes maximum standard contribution amount as well as catch-up contributions for individuals 50 and over.';
  }
  return 'Limit includes maximum standard contribution amount.';
};

export const FourOhOneKContributionsSummary: React.FC<{
  employeeContributions: EmployeeFourOhOneKContributionsResult;
  employers: EmployersResult;
  hasSolo401k: boolean;
}> = ({ employeeContributions, employers, hasSolo401k }) => {
  const status = fourOhOneKLimitMaxOutProjection(employeeContributions);
  const iraContributionTooltipLimitCopy = fourOhOneKLimitTooltipCopy(employeeContributions);

  return (
    <Box>
      <ListRow css={{ padding: 'space.25' }}>
        <ListRow.LeadingContent>
          <Box css={{ display: 'flex', flexDirection: 'row' }}>
            <Text>
              {employeeContributions.thisYear.taxYear.year} {hasSolo401k && 'employee'} contributions
            </Text>
            <Tooltip>
              <Tooltip.Trigger asChild={true} style={{ paddingLeft: '4px' }}>
                <Tooltip.IconButton data-testid="contributionTooltip" />
              </Tooltip.Trigger>
              <Tooltip.Content align="start" side="right">
                <Tooltip.Text>{iraContributionTooltipLimitCopy}</Tooltip.Text>
              </Tooltip.Content>
            </Tooltip>
          </Box>
          {status === MAXED && (
            <Text css={{ textAlign: 'left' }} color="text.success" variant="text50">
              Maxed out
            </Text>
          )}
          {status === PROJECTED_TO_MAX && (
            <Text css={{ textAlign: 'left' }} color="text.success" variant="text50">
              Projected to max out
            </Text>
          )}
          {status === NOT_PROJECTED_TO_MAX && (
            <Text css={{ textAlign: 'left' }} color="text.weak" variant="text50">
              Limit: {formatMoney(employeeContributions.thisYear.taxYear.fourOhOneKContributionLimit)}
            </Text>
          )}
        </ListRow.LeadingContent>
        <ListRow.TrailingContent>
          <Text css={{ textAlign: 'right' }}>{formatMoney(employeeContributions.thisYear.result.amount)}</Text>
        </ListRow.TrailingContent>
      </ListRow>
      {employers.map(employer => {
        if (hideEmployerMatch(employer)) {
          return null;
        }

        return (
          <Box key={employer.id}>
            <Separator css={{ my: 'space.200' }} />
            <Box css={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box css={{ display: 'flex', flexDirection: 'column' }}>
                {EmployerMatchHeader(employer, employeeContributions)}
                {employers.length > 1 && (
                  <Text color="text.weak" variant="text50">
                    {employer.name}
                  </Text>
                )}
              </Box>
              <Box css={{ display: 'flex', flexDirection: 'column' }}>
                <Text css={{ textAlign: 'right' }}>{formatMoney(toMoney(displayMatchAmount(employer)))}</Text>
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};
